import { useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { TabItemConfig } from '@deltek/specification-client/core/Navigation/TopTabNav'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import clsx from 'clsx'
import StudioTopTabNav from 'pages/StudioPage/components/StudioTopTabNav'
import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import useArrowKeyNav from 'shared/hooks/useArrowKeyNav'
import { useHarmonyTabStyles } from 'shared/hooks/useHarmonyTabStyles'
import useIsHarmonyHeader from 'shared/hooks/useIsHarmonyHeader'
import {
    getSessionValue,
    storeInSessionStorage,
} from 'shared/utils/sessionStorageUtils'
import { processEnv } from '../../../components/configuration/environmentVariables'

const useStyles = makeStyles((theme: Theme) => ({
    tabButton: {
        fontWeight: 'normal',
        borderRadius: theme.spacing(100),
        color: theme.palette.common.white,
        '&:hover': {
            color: theme.palette.common.white,
        },
    },
    disabledTabButton: {
        '& span': {
            color: 'rgba(255, 255, 255, 0.25)',
        },
    },
    normalSizeTabs: {
        fontSize: pxToRem(18),
        padding: theme.spacing(0.5, 1),
        fontWeight: 400,
    },
    normalSizeTabsHarmony: {
        fontSize: pxToRem(16),
    },
    largeSizeTabs: {
        fontWeight: 400,
        fontSize: pxToRem(24),
        padding: theme.spacing(0.75, 2),
    },
    activeTabButton: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white,
        '&:hover': {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
        },
    },

    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
}))

enum ProjectTabs {
    Overview = 'overview',
    Schedule = 'milestones',
    Workspace = 'workspace',
    Reports = 'reports',
    Import = 'import',
    Publish = 'publish',
    Format = 'format',
    ElementLibrary = 'elementlibrary',
    Dashboard = 'dashboard',
}

export const TabItems: TabItemConfig[] = [
    {
        label: 'Overview',
        value: ProjectTabs.Overview,
    },
    {
        label: 'Schedule',
        value: ProjectTabs.Schedule,
    },
    {
        label: 'Workspace',
        value: ProjectTabs.Workspace,
    },
    {
        label: 'Reports',
        value: ProjectTabs.Reports,
    },
    {
        label: 'Import',
        value: ProjectTabs.Import,
    },
    {
        label: 'Format',
        value: ProjectTabs.Format,
    },
    {
        label: 'Export',
        value: ProjectTabs.Publish,
    },
]

export interface ProjectTopTabNavProps {
    /**
     * Determines whether the component expands its horizontal spacing
     * to take up the full width of its container.
     */
    expandToHorizontalSpace?: boolean

    /**
     * Determines whether to use original tab font and background sizes
     * or the bigger variants.
     */
    useBiggerTabs?: boolean
    showExport?: VoidFunction
}

interface LocationState {
    from: string
    fromStudio?: boolean
    selectedElementIdFromDesignProgressTable?: string
}

const ariaCurrentPage = (isActive: boolean) => (isActive ? 'page' : undefined)

const ProjectTopTabNav: React.FC<ProjectTopTabNavProps> = ({
    expandToHorizontalSpace = false,
    useBiggerTabs = false,
    showExport,
}) => {
    const classes = useStyles()
    const harmonyClasses = useHarmonyTabStyles()
    const { isReviewer } = useReactiveVar(userInfo)
    const isHarmonyHeader = useIsHarmonyHeader()
    const { containerRef, onKeyDown } = useArrowKeyNav<HTMLDivElement>('a')

    const { pathname, state } = useLocation()
    const { currentUserActiveFirm } = React.useContext(AppBarContext)

    // Persist bestPracticeProjectId in sessionStorage so the correct navigation menu renders correctly.
    React.useEffect(() => {
        if (currentUserActiveFirm?.bestPracticeProjectId) {
            storeInSessionStorage(
                'sp_bpProjectId',
                currentUserActiveFirm.bestPracticeProjectId
            )
        }
    }, [currentUserActiveFirm?.bestPracticeProjectId])

    const activeBPProjectId = React.useMemo(
        () =>
            currentUserActiveFirm?.bestPracticeProjectId ??
            (getSessionValue('sp_bpProjectId') as string | null),
        [currentUserActiveFirm?.bestPracticeProjectId]
    )

    const isRedirect = React.useMemo(() => {
        if (
            pathname.includes(ProjectTabs.Workspace) &&
            !pathname.endsWith(ProjectTabs.Workspace)
        ) {
            return true
        }
        return false
    }, [pathname])

    const isUserRedirectedFromStudio = React.useMemo(
        () => (state as LocationState)?.fromStudio,
        [state]
    )

    const onPublishClick = React.useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault()
            showExport?.()
        },
        [showExport]
    )

    const isTabDisplayed = React.useCallback(
        (tabItem) => {
            if (!!isReviewer && tabItem.value === ProjectTabs.Import) {
                return false
            }
            return true
        },
        [isUserRedirectedFromStudio, ProjectTabs]
    )

    // See useHarmonyTabStyles for why dark/light classes are never combined.
    const getTabButtonClassName = (isActive: boolean) =>
        clsx(
            isHarmonyHeader ? harmonyClasses.tabButtonLight : classes.tabButton,
            useBiggerTabs ? classes.largeSizeTabs : classes.normalSizeTabs,
            !useBiggerTabs && isHarmonyHeader && classes.normalSizeTabsHarmony,
            isActive &&
                (isHarmonyHeader
                    ? harmonyClasses.activeTabButtonLight
                    : classes.activeTabButton)
        )

    const disabledTabButtonClassName = isHarmonyHeader
        ? harmonyClasses.disabledTabButtonLight
        : classes.disabledTabButton

    return (
        <>
            {isUserRedirectedFromStudio ||
            pathname.includes('/DraftSpecTextProject/') ||
            pathname.includes('/DraftMasterSpecProject/') ||
            pathname.includes(`/${activeBPProjectId}/`) ? (
                <StudioTopTabNav />
            ) : (
                <GridContainer
                    // Harmony always uses the tight, centered layout (matching StudioTopTabNav's
                    // Harmony sizing) regardless of expandToHorizontalSpace, which is a
                    // classic-header-only affordance for spreading tabs across the full width.
                    spacing={
                        isHarmonyHeader || expandToHorizontalSpace ? 0 : 2
                    }
                    justifyContent={
                        isHarmonyHeader
                            ? 'center'
                            : expandToHorizontalSpace
                            ? 'space-between'
                            : 'center'
                    }
                    alignItems="center"
                    data-testid="projectTopTabNav"
                    ref={containerRef}
                    onKeyDown={onKeyDown}
                >
                    {TabItems.map((tabItem: TabItemConfig, index: number) => {
                        return isTabDisplayed(tabItem) ? (
                            <GridItem
                                data-testid={`topTabNav-${tabItem.label}`}
                                key={`tabItem.value-${index}`}
                            >
                                {tabItem.value === ProjectTabs.Publish ? (
                                    <Button
                                        component={NavLink}
                                        aria-current={ariaCurrentPage(
                                            pathname.endsWith(tabItem.value)
                                        )}
                                        to={tabItem.value}
                                        className={getTabButtonClassName(
                                            pathname.endsWith(tabItem.value)
                                        )}
                                        classes={{
                                            disabled: disabledTabButtonClassName,
                                        }}
                                        disabled={isReviewer}
                                        onClick={
                                            !isReviewer
                                                ? onPublishClick
                                                : undefined
                                        }
                                    >
                                        {tabItem.label}
                                    </Button>
                                ) : (
                                    <Button
                                        component={NavLink}
                                        key={`tabItem.value-${index}`}
                                        aria-current={ariaCurrentPage(
                                            pathname.endsWith(tabItem.value) ||
                                                (isRedirect &&
                                                    tabItem.value.includes(
                                                        ProjectTabs.Workspace
                                                    ))
                                        )}
                                        to={
                                            !isRedirect
                                                ? tabItem.value
                                                : `${pathname.slice(
                                                      0,
                                                      pathname.indexOf(
                                                          ProjectTabs.Workspace
                                                      )
                                                  )}${tabItem.value}`
                                        }
                                        className={getTabButtonClassName(
                                            pathname.endsWith(tabItem.value) ||
                                                (isRedirect &&
                                                    tabItem.value.includes(
                                                        ProjectTabs.Workspace
                                                    ))
                                        )}
                                    >
                                        {tabItem.label}
                                    </Button>
                                )}
                            </GridItem>
                        ) : tabItem.value === ProjectTabs.Import ? (
                            processEnv.REACT_APP_ShowImportTab === '1' &&
                            !isReviewer ? (
                                <GridItem
                                    data-testid={`topTabNav-${tabItem.label}`}
                                    key={`tabItem.value-${index}`}
                                >
                                    <Button
                                        component={NavLink}
                                        key={`tabItem.value-${index}`}
                                        aria-current={ariaCurrentPage(
                                            pathname.endsWith(tabItem.value)
                                        )}
                                        to={tabItem.value}
                                        className={getTabButtonClassName(
                                            pathname.endsWith(tabItem.value)
                                        )}
                                    >
                                        {tabItem.label}
                                    </Button>
                                </GridItem>
                            ) : null
                        ) : processEnv.REACT_APP_ShowFormat === '1' ? (
                            <GridItem
                                data-testid={`topTabNav-${tabItem.label}`}
                                key={`tabItem.value-${index}`}
                            >
                                <Button
                                    component={NavLink}
                                    aria-current={ariaCurrentPage(
                                        pathname.endsWith(tabItem.value)
                                    )}
                                    to={tabItem.value}
                                    className={getTabButtonClassName(
                                        pathname.endsWith(tabItem.value)
                                    )}
                                    classes={{
                                        disabled: disabledTabButtonClassName,
                                    }}
                                    onClick={
                                        tabItem.value === ProjectTabs.Publish
                                            ? onPublishClick
                                            : () => {
                                                  return undefined
                                              }
                                    }
                                >
                                    {tabItem.label}
                                </Button>
                            </GridItem>
                        ) : (
                            !isReviewer &&
                            tabItem.value === ProjectTabs.Import &&
                            [
                                processEnv.REACT_APP_ShowImportTab,
                                processEnv.REACT_APP_ShowFormat,
                            ].map(
                                (envVars: string, i) =>
                                    envVars === '1' && (
                                        <GridItem
                                            data-testid={`topTabNav-${tabItem.label}`}
                                            key={`tabItem.value-${index}`}
                                        >
                                            <Button
                                                component={NavLink}
                                                key={`tabItem.value-${index}`}
                                                aria-current={ariaCurrentPage(
                                                    pathname.endsWith(
                                                        tabItem.value
                                                    )
                                                )}
                                                to={tabItem.value}
                                                className={getTabButtonClassName(
                                                    pathname.endsWith(
                                                        tabItem.value
                                                    )
                                                )}
                                            >
                                                {tabItem.label}
                                            </Button>
                                        </GridItem>
                                    )
                            )
                        )
                    })}
                </GridContainer>
            )}
        </>
    )
}

export default ProjectTopTabNav
